<template>
	<LayoutScreen background="--background-base" max-width="500px">
		<template #header>
			<LayoutFlex padding="0 var(--size-20)" gap="0">
				<CancelHeader />
			</LayoutFlex>
		</template>

		<template #content>
			<!-- Figma 7415:18014: the banner 24 under the logo row; Submit pinned to the bottom (design review, 2026-10-22). -->
			<LayoutFlex v-if="isCommitment" padding="var(--size-24) var(--size-20)" gap="var(--size-24)">
				<LayoutFlex
					flex-direction="row"
					align-items="center"
					gap="var(--size-16)"
					padding="var(--size-12) var(--size-16)"
					:background="KOVA.purpleBg"
					corner-radius="var(--size-16)"
				>
					<span class="scope__warning"><img :src="warningTriangle" alt="" width="18.8939" height="16.4078" /></span>
					<Text font="--text-14-20-semi" :color="KOVA.purple">
						Your prescription is currently under clinical review. Canceling your Kova plan now will stop this process.
					</Text>
				</LayoutFlex>
				<Text font="--heading-h3" color="--text-dark-primary">What would you like to do?</Text>
				<Select type="single" :value="selected" gap="var(--size-8)" :commit-delay-ms="0" @ds:change="onChange">
					<template #options>
						<SelectItem v-for="o in commitmentScopes" :key="o.value" :value="o.value">
							<LayoutFlex padding="0" gap="var(--size-12)" align-items="flex-start">
								<Text font="--text-16-20-semi" color="--text-dark-primary">{{ o.label }}</Text>
								<Text font="--text-14-20-med" :color="KOVA.textSecondary">{{ o.description }}</Text>
							</LayoutFlex>
							<SelectIndicator mode="always" />
						</SelectItem>
					</template>
				</Select>
			</LayoutFlex>

			<LayoutFlex v-else padding="var(--size-24) var(--size-20)" gap="var(--size-28)" align-items="center">
				<Text font="--heading-h2" color="--text-dark-primary" text-align="center">
					Your prescription is currently under clinical review
				</Text>
				<Text font="--text-18-22-bold" color="--text-dark-primary" text-align="center">
					Choose what you’d like to cancel
				</Text>

				<!-- :commit-delay-ms="0": DS ≥0.36 delays single-select commits 250 ms by default;
     the selection gates the Submit CTA, so the commit must be instant. -->
				<Select type="single" :value="selected" gap="var(--size-8)" :commit-delay-ms="0" @ds:change="onChange">
					<template #options>
						<SelectItem v-for="o in SCOPES" :key="o.value" :value="o.value">
							<Text font="--text-16-20-semi" color="--text-dark-primary">{{ o.label }}</Text>
							<SelectIndicator mode="always" />
						</SelectItem>
					</template>
				</Select>
			</LayoutFlex>
		</template>

		<template #footer>
			<LayoutFlex padding="0 var(--size-20) var(--size-24)" gap="0">
				<Button full-width :disabled="!geo.isOneClickCancelRequired && !selected" @ds:click="onSubmit">
					<Text font="--text-16-18-semi">Submit</Text>
				</Button>
			</LayoutFlex>
		</template>
	</LayoutScreen>
</template>

<script setup lang="ts">
// Figma `kova cancel flow1` frame C (30320:19371) — the extra step shown *before* the existing
// survey when the prescription is still under clinical review. Reached from the "Before you go…"
// modal on /status ("Continue with cancellation"); on Submit the flow rejoins the normal path at
// /cancel/reasons.
//
// A commitment plan under review (DEV-35727, Figma `new2` 6562:4566) opens here straight from
// /status, with the banner copy and option descriptions of the new design, and branches on Submit:
// "Cancel my KOVA plan now" → /cancel/confirm (`hard`), "Cancel future renewals only" → the survey,
// whose Continue on /cancel/likelihood is the `soft` cancel. See `afterScope`.
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import {
	Button,
	LayoutFlex,
	LayoutScreen,
	Select,
	SelectIndicator,
	SelectItem,
	Text,
} from '@simple-life-app/growth-ui-kova'
import CancelHeader from '@/components/CancelHeader.vue'
import warningTriangle from '@/assets/icons/warning-triangle.svg'
import type { CancellationScope } from '@/api/medBilling'
import { afterScope } from '@/cancelFlow/commitment'
import { formatDate } from '@/cancelFlow/format'
import { useCancelFlowStore } from '@/store/cancelFlow'
import { useGeoStore } from '@/store/geo'
import { useSurveyStore } from '@/store/survey'
import { KOVA } from '@/designTokens'
import { STEP_TITLE, useCancelStepLogging } from '@/analytics'

const router = useRouter()
const survey = useSurveyStore()
const geo = useGeoStore()
const flow = useCancelFlowStore()

const isCommitment = computed(() => flow.stage === 'review')

// Copy and order mirror the design. The `value` slugs double as the analytics `data.responses`
// ids and as the answer `/cancel/confirm` turns into `cancellation_type` via
// `cancellationTypeForScope` — see docs/architecture/analytics.md and ADR 0013.
const SCOPES: ReadonlyArray<{ value: CancellationScope; label: string }> = [
	{ value: 'cancel_my_current_plan', label: 'Cancel my current Kova plan and prescription' },
	{ value: 'cancel_future_renewals_only', label: 'Cancel future renewals only' },
]

const commitmentScopes = computed<ReadonlyArray<{ value: CancellationScope; label: string; description: string }>>(
	() => [
		{
			value: 'cancel_my_current_plan',
			label: 'Cancel my KOVA plan now',
			description: 'Your prescription review will stop, and any pending orders will be canceled.',
		},
		{
			value: 'cancel_future_renewals_only',
			label: 'Cancel future renewals only',
			// What a `soft` cancel does under review: `end_of_commitment` on a monthly commitment, `end_of_period`
			// on a paid-in-full plan (no cycle to wind down — the paid period runs out).
			description: `Your plan stays active until ${formatDate(flow.quotes.keep?.access_until ?? flow.quotes.endOfPeriod?.access_until)}. You’ll get your remaining orders, and it won’t renew after that`,
		},
	],
)

const selected = computed<string>(() => {
	const raw = survey.answers['cancellation_scope']
	return typeof raw === 'string' ? raw : ''
})

const step = useCancelStepLogging(STEP_TITLE.PRESCRIPTION_IS_UNDER_REVIEW, {
	getResponses: () => (selected.value ? [selected.value] : []),
})

function onChange(next: string | string[]) {
	const value = Array.isArray(next) ? (next[0] ?? '') : next
	survey.setAnswer('cancellation_scope', value)
	step.record(value ? [value] : [])
}

function onSubmit() {
	// No network call here: the answer only exists to pick the cancel call's `cancellation_type`,
	// which `/cancel/confirm` reads back out of the survey store at the end of the flow (ADR 0013).
	// California one-click cancel: an empty selection must still move the flow along — it falls
	// through to `soft` there, same exception the other survey screens make (see geo store
	// `isOneClickCancelRequired`).
	if (isCommitment.value) {
		router.push(afterScope(selected.value as CancellationScope | ''))
		return
	}
	router.push('/cancel/reasons')
}
</script>

<style scoped>
/* Figma icon-warning: a 20px box, the triangle inset 1px from the top and 0.5px from the left. */
.scope__warning {
	position: relative;
	flex-shrink: 0;
	width: var(--size-20);
	height: var(--size-20);
}

.scope__warning img {
	position: absolute;
	top: 1px;
	left: 0.5px;
}
</style>
